import { Instagram, Play } from "lucide-react";

const REEL_URL =
  "https://www.instagram.com/reel/Dc0nrp9tfjz/?stkn=MTlwaW8zOXY4NG1keg==";

// Lightweight, zero-JS-cost Instagram Reel card.
// Uses a local WebP as backdrop + a Play + Instagram overlay. On click it opens
// the Reel in a new tab. No official IG embed (would add ~200KB of blocking JS
// and hurt mobile LCP).
const InstagramReelCard = ({ thumbnail, lang = "fr" }) => {
  const isFr = lang === "fr";
  return (
    <section
      data-testid="ig-reel-card-section"
      className="max-w-[900px] mx-auto"
    >
      <div className="text-center mb-6">
        <div className="eyebrow text-[#8B6343] mb-3">
          {isFr ? "Vu en vrai" : "Seen in the wild"}
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
          {isFr
            ? "Vu en vrai sur Instagram"
            : "Seen for real on Instagram"}
        </h2>
        <p className="mt-3 text-[15px] text-[#4a4a4a] max-w-xl mx-auto leading-relaxed">
          {isFr
            ? "Une utilisatrice teste le coussin en conditions réelles — 30 secondes chrono."
            : "A real user tests the cushion in real conditions — 30 seconds."}
        </p>
      </div>

      <a
        href={REEL_URL}
        target="_blank"
        rel="noopener noreferrer nofollow"
        data-testid="ig-reel-card-link"
        aria-label={isFr ? "Voir le Reel sur Instagram" : "Watch the Reel on Instagram"}
        className="group relative block mx-auto max-w-[420px] rounded-2xl overflow-hidden shadow-[0_18px_50px_-20px_rgba(61,43,31,0.55)] transition-transform duration-500 hover:-translate-y-1"
      >
        {/* Instagram gradient border */}
        <div className="absolute inset-0 rounded-2xl p-[3px] bg-[conic-gradient(from_200deg_at_50%_50%,#F58529_0deg,#DD2A7B_120deg,#8134AF_240deg,#515BD4_360deg)] z-0" />
        <div className="relative rounded-[calc(1rem-3px)] overflow-hidden bg-black z-10 m-[3px]">
          {/* 9:16 Reel aspect */}
          <div className="aspect-[9/16] relative">
            <img
              src={thumbnail}
              alt={isFr ? "Aperçu du Reel Instagram — Coussin Massant myFLOVEA" : "Instagram Reel preview — myFLOVEA Massage Cushion"}
              loading="lazy"
              decoding="async"
              width="720"
              height="1280"
              className="absolute inset-0 w-full h-full object-cover scale-110 transition-transform duration-700 group-hover:scale-105"
            />
            {/* Vignette for legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/40" />

            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 flex items-center justify-center shadow-[0_10px_40px_-8px_rgba(0,0,0,0.6)] transition-transform duration-300 group-hover:scale-110">
                <Play
                  size={34}
                  strokeWidth={0}
                  fill="#1A1A1A"
                  className="translate-x-0.5"
                />
              </div>
            </div>

            {/* Instagram badge top-left */}
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[11px] tracking-[0.14em] uppercase text-[#1A1A1A] font-medium">
              <Instagram size={13} strokeWidth={2} />
              Reel
            </div>

            {/* Caption bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
              <div className="text-[13px] tracking-[0.14em] uppercase opacity-80 mb-1">
                {isFr ? "Voir sur Instagram" : "Watch on Instagram"}
              </div>
              <div className="font-serif-display text-xl leading-tight">
                {isFr
                  ? "« Franchement, c'est bluffant. »"
                  : "\u201CHonestly, it's stunning.\u201D"}
              </div>
            </div>
          </div>
        </div>
      </a>

      <div className="mt-5 text-center text-[12px] text-[#8a8378]">
        {isFr
          ? "S'ouvre sur Instagram · Aucun compte requis"
          : "Opens on Instagram · No account required"}
      </div>
    </section>
  );
};

export default InstagramReelCard;
